import React, { useEffect, useState } from 'react';
import api from '../api';

/**
 * ReminderBell — one-tap notify-me toggle for pre-launch Clans.
 *
 * Wraps the shared `/api/clans/{handle}/remind{/me}` endpoints and renders
 * either a compact icon (`variant="icon"`, for detail pages next to Join/
 * Message/Bookmark) or a chip with a count (`variant="chip"`, for launch
 * cards where the reminder count is a social-proof signal).
 *
 * `initialCount` lets callers seed the count they already fetched (avoids a
 * flash of "0" while the mount-time GET is in flight) and `onCountChange`
 * lets a parent list keep its local state in sync after a toggle.
 *
 * `stopPropagation` is set to `true` by default so the bell can safely sit
 * inside a card that's itself clickable (e.g. Launches cards navigate on
 * click) — the bell tap should never accidentally navigate.
 */
export default function ReminderBell({
  handle,
  variant = 'icon',
  initialCount = 0,
  initialReminded = null,
  onCountChange,
  className = '',
  size = 'md',
  stopPropagation = true,
}) {
  const [reminded, setReminded] = useState(!!initialReminded);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(initialReminded !== null);

  useEffect(() => {
    if (!handle || initialReminded !== null) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/clans/${handle}/remind/me`);
        if (!alive) return;
        setReminded(!!data.you_are_reminded);
        if (typeof data.reminder_count === 'number') setCount(data.reminder_count);
        setLoaded(true);
      } catch { /* legacy clan may 404 — silent */ }
    })();
    return () => { alive = false; };
  }, [handle, initialReminded]);

  const toggle = async (e) => {
    if (stopPropagation) { e.stopPropagation(); e.preventDefault?.(); }
    if (!handle || busy) return;
    setBusy(true);
    try {
      const url = `/clans/${handle}/remind`;
      const { data } = reminded ? await api.delete(url) : await api.post(url);
      setReminded(!!data.you_are_reminded);
      if (typeof data.reminder_count === 'number') {
        setCount(data.reminder_count);
        onCountChange?.(data.reminder_count, !!data.you_are_reminded);
      }
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  const label = reminded ? 'Remove launch reminder' : 'Notify me at launch';
  const title = reminded
    ? `You're on the list — ${count} reminded · tap to remove`
    : `Notify me at launch · ${count} already reminded`;

  if (variant === 'chip') {
    return (
      <button
        type="button"
        className={`reminder-bell reminder-bell-chip${reminded ? ' active' : ''} ${className}`}
        onClick={toggle}
        disabled={busy || !loaded}
        aria-pressed={reminded}
        aria-label={label}
        title={title}
        data-testid={`reminder-bell-${handle}`}
      >
        <span className="reminder-bell-icon" aria-hidden="true">🔔</span>
        <span className="reminder-bell-txt">
          {reminded ? 'Reminded' : 'Notify me'}
        </span>
        {count > 0 && <span className="reminder-bell-count">{count}</span>}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`reminder-bell reminder-bell-icon reminder-bell-${size}${reminded ? ' active' : ''} ${className}`}
      onClick={toggle}
      disabled={busy || !loaded}
      aria-pressed={reminded}
      aria-label={label}
      title={title}
      data-testid={`reminder-bell-${handle}`}
    >
      <span className="reminder-bell-icon" aria-hidden="true">🔔</span>
      {count > 0 && <span className="reminder-bell-count">{count}</span>}
    </button>
  );
}
